import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, serverTimestamp, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

function RegisterPage() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is already logged in, redirect to dashboard
        navigate("/doctor", { replace: true });
      } else {
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Show loading while checking auth state
  if (checkingAuth) {
    return (
      <div className="register-layout">
        <div className="register-content" style={{ textAlign: "center" }}>
          <div
            className="loading-spinner"
            style={{ margin: "0 auto 16px" }}
          ></div>
          <p style={{ color: "var(--register-muted)" }}>
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  const toFriendlyError = (err) => {
    const code = err?.code || "";
    switch (code) {
      case "auth/operation-not-allowed":
        return "Email/Password sign-up is disabled. Enable it in Firebase Console → Authentication → Sign-in method.";
      case "auth/invalid-api-key":
        return "Invalid Firebase API key. Check your .env values match the Firebase project.";
      case "auth/domain-not-allowed":
        return "This domain is not authorized for Firebase Auth. Add your dev domain (e.g., localhost) in Authentication → Settings → Authorized domains.";
      case "auth/network-request-failed":
        return "Network error. Check your connection and try again.";
      case "auth/weak-password":
        return "Password is too weak. Use at least 8 characters.";
      default:
        return err?.message || "Something went wrong. Please try again.";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      await updateProfile(userCredential.user, { displayName: fullName });
      await setDoc(doc(db, "users", userCredential.user.uid), {
        fullName,
        email,
        role: "doctor",
        createdAt: serverTimestamp(),
      });

      // Don't set success message or reset loading here
      // Just navigate immediately
      navigate("/doctor", { replace: true });
    } catch (err) {
      setError(toFriendlyError(err));
      setLoading(false); // Only reset loading on error
    }
  };

  const handleGoogleSignup = async () => {
    setError("");
    setSuccess("");
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user doc exists, if not create one
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          fullName: user.displayName || "Doctor",
          email: user.email,
          role: "doctor",
          createdAt: serverTimestamp(),
        });
      }

      navigate("/doctor", { replace: true });
    } catch (err) {
      setError(toFriendlyError(err));
      setLoading(false);
    }
  };

  return (
    <div className="register-layout">
      <div className="register-card">
        <header className="register-topbar">
          <button
            type="button"
            className="register-icon-btn"
            aria-label="Go back"
            onClick={() => navigate(-1)}
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h2>Register</h2>
          <span className="register-topbar-spacer" />
        </header>

        <div className="register-body">
          <div className="register-logo">
            <img
              src="/logo.png"
              alt="PhysioKea"
              className="register-logo-image"
            />
          </div>

          <h1 className="register-title">Join PhysioKea</h1>
          <p className="register-subtitle">
            Manage your patients and appointments efficiently. Create your
            professional account below.
          </p>

          <form className="register-form" onSubmit={handleSubmit}>
            <label className="register-field">
              <span className="register-label">Full Name</span>
              <div className="register-input">
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Dr. John Doe"
                  required
                />
                <span
                  className="material-symbols-outlined register-input-icon"
                  aria-hidden="true"
                >
                  person
                </span>
              </div>
            </label>

            <label className="register-field">
              <span className="register-label">Email Address</span>
              <div className="register-input">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john.doe@example.com"
                  required
                />
                <span
                  className="material-symbols-outlined register-input-icon"
                  aria-hidden="true"
                >
                  mail
                </span>
              </div>
            </label>

            <label className="register-field">
              <span className="register-label">Password</span>
              <div className="register-input">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  required
                />
                <button
                  type="button"
                  className="register-icon-btn ghost"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <span className="material-symbols-outlined">
                    {showPassword ? "visibility" : "visibility_off"}
                  </span>
                </button>
              </div>
            </label>

            <label className="register-field">
              <span className="register-label">Confirm Password</span>
              <div className="register-input">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                />
                <button
                  type="button"
                  className="register-icon-btn ghost"
                  aria-label={
                    showConfirmPassword
                      ? "Hide confirmation"
                      : "Show confirmation"
                  }
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  <span className="material-symbols-outlined">
                    {showConfirmPassword ? "visibility" : "visibility_off"}
                  </span>
                </button>
              </div>
            </label>

            <label className="register-checkbox">
              <input type="checkbox" required />
              <div>
                <span className="register-checkbox-title">
                  I agree to the Terms & Conditions
                </span>
                <p className="register-checkbox-copy">
                  By registering, you agree to our Terms of Service and Privacy
                  Policy.
                </p>
              </div>
            </label>

            {error && <p className="feedback error">{error}</p>}
            {success && <p className="feedback success">{success}</p>}

            <button type="submit" className="register-cta" disabled={loading}>
              {loading ? (
                <>
                  <div className="loading-spinner" />
                  <span>Creating account...</span>
                </>
              ) : (
                <span>Create Account</span>
              )}
            </button>

            <div className="login-divider" style={{ margin: "16px 0" }}>
              <span>Or sign up with</span>
            </div>

            <button
              type="button"
              className="login-alt"
              onClick={handleGoogleSignup}
              disabled={loading}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 48 48"
                style={{ marginRight: 8 }}
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
              </svg>
              <span>Continue with Google</span>
            </button>
          </form>

          <p className="register-footer">
            Already a member? <Link to="/login">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
